import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Typography,
  Chip,
  Avatar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import SpeedIcon from '@mui/icons-material/Speed';
import TimerIcon from '@mui/icons-material/Timer';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getUserPlanUsage } from '../services/userRobotApi';
import theme from '../UserDashboard/theme';

const accent = theme.colors.brandDark;

const DEFAULT_PLAN_USAGE = {
  planName: 'Current Plan',
  user: '',
  robotId: '',
  usage: 0,
  limit: 0,
  remaining: 0,
  usedSeconds: 0,
  remainingSeconds: 0,
  resetDate: null,
  status: 'ok',
  usedTimeFormatted: '0h 0m 0s',
  remainingTimeFormatted: '0h 0m 0s',
  usagePercentage: 0,
  planAddonStatus: '',
  planpaymentStatus: '',
  canDisinfect: true,
  alert: null,
  message: null,
  pools: null,
  poolStatuses: null,
  monthlyUsage: [],
};

const NEAR_LIMIT_THRESHOLD = 80;

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeStatus = (status, usagePercent) => {
  const value = String(status || '').toLowerCase();
  if (value === 'ok' || value === 'warning' || value === 'exceeded') return value;
  if (usagePercent >= 100) return 'exceeded';
  if (usagePercent >= NEAR_LIMIT_THRESHOLD) return 'warning';
  return 'ok';
};

function pickPlanUsagePayload(raw, robotId) {
  let body = raw;
  if (raw?.success && raw?.data != null) {
    body = raw.data;
  } else if (raw?.data?.success && raw?.data?.data != null) {
    body = raw.data.data;
  }

  if (Array.isArray(body)) {
    if (!body.length) return {};
    if (robotId) {
      const match = body.find(
        (r) =>
          r?.robotId === robotId ||
          (Array.isArray(r?.robotIds) && r.robotIds.includes(robotId)),
      );
      if (match) return match;
    }
    return body[0];
  }

  return body && typeof body === 'object' ? body : {};
}

const secondsToHours = (seconds) => toNumber(seconds, 0) / 3600;

function formatDuration(seconds) {
  const total = Math.max(toNumber(seconds, 0), 0);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h}h ${m}m ${s}s`;
}

function normalizeMonthlyUsageRow(row) {
  const mainUsedSeconds = toNumber(row.mainUsedSeconds, 0);
  const addonUsedSeconds = toNumber(row.addonUsedSeconds, 0);
  const adminUsedSeconds = toNumber(row.adminUsedSeconds, 0);
  const usedSeconds =
    toNumber(row.usedSeconds, 0) || mainUsedSeconds + addonUsedSeconds + adminUsedSeconds;

  const label =
    row.label ||
    (row.planMonth != null
      ? `Month ${row.planMonth}`
      : row.monthName && row.year
        ? `${row.monthName} ${row.year}`
        : '—');

  return {
    planMonth: row.planMonth ?? null,
    month: row.month,
    monthName: row.monthName,
    year: row.year,
    label,
    mainUsedSeconds,
    addonUsedSeconds,
    adminUsedSeconds,
    usedSeconds,
    mainUsedFormatted: row.mainUsedFormatted || formatDuration(mainUsedSeconds),
    addonUsedFormatted: row.addonUsedFormatted || formatDuration(addonUsedSeconds),
    adminUsedFormatted: row.adminUsedFormatted || formatDuration(adminUsedSeconds),
    totalUsedFormatted: row.totalUsedFormatted || formatDuration(usedSeconds),
    mainHours: secondsToHours(mainUsedSeconds),
    addonHours: secondsToHours(addonUsedSeconds),
    adminHours: secondsToHours(adminUsedSeconds),
    totalHours: secondsToHours(usedSeconds),
  };
}

const normalizePlanUsage = (raw, robotId) => {
  const payload = pickPlanUsagePayload(raw, robotId);
  if (payload.message && !payload.pools && !payload.usedSeconds) {
    return {
      ...DEFAULT_PLAN_USAGE,
      user: payload.user || '',
      robotId: payload.robotId || robotId || '',
      message: payload.message,
      status: 'exceeded',
      canDisinfect: false,
    };
  }

  const pools = payload.pools || null;
  const usedSeconds = toNumber(
    payload.usedSeconds ?? pools?.totalUsedSeconds,
    0,
  );
  const remainingSeconds = toNumber(
    payload.remainingSeconds ?? pools?.totalRemainingSeconds,
    0,
  );
  const totalCombinedSeconds = usedSeconds + remainingSeconds;

  const usage = secondsToHours(usedSeconds);
  const remaining = secondsToHours(remainingSeconds);
  const limit = toNumber(
    payload.totalHoursPerMonth ??
      payload.limit ??
      (totalCombinedSeconds > 0 ? totalCombinedSeconds / 3600 : 0),
    0,
  );

  const usagePercent = toNumber(
    payload.usagePercentage,
    totalCombinedSeconds > 0
      ? Math.min((usedSeconds / totalCombinedSeconds) * 100, 100)
      : limit > 0
        ? Math.min((usage / limit) * 100, 100)
        : 0,
  );

  const canDisinfect =
    payload.canDisinfect !== undefined
      ? Boolean(payload.canDisinfect)
      : remainingSeconds > 0;

  let status = normalizeStatus(payload.status, usagePercent);
  if (!canDisinfect) status = 'exceeded';
  else if (toNumber(usagePercent) >= 90) status = 'warning';

  const poolStatuses = payload.poolStatuses || {
    main: pools?.main?.status,
    addon: pools?.addon?.status,
    admin: pools?.admin?.status,
  };

  const planName =
    payload.planName ||
    payload.plan ||
    (payload.totalHoursPerMonth
      ? `${payload.totalHoursPerMonth}h Monthly Plan`
      : DEFAULT_PLAN_USAGE.planName);

  return {
    planName,
    user: payload.user || '',
    robotId: payload.robotId || robotId || '',
    usage,
    limit,
    remaining: Math.max(remaining, 0),
    usedSeconds,
    remainingSeconds,
    resetDate: payload.resetDate || payload.renewalDate || payload.nextBilling || null,
    status,
    usedTimeFormatted:
      payload.usedTimeFormatted || `${toNumber(usage).toFixed(2)} hrs`,
    remainingTimeFormatted:
      payload.remainingTimeFormatted || `${toNumber(remaining).toFixed(2)} hrs`,
    usagePercentage: Math.min(Math.max(usagePercent, 0), 100),
    planAddonStatus:
      payload.planAddonStatus ||
      (poolStatuses.addon === 'active' ? 'active' : poolStatuses.addon || ''),
    planpaymentStatus: payload.planpaymentStatus || '',
    canDisinfect,
    alert: payload.alert || null,
    message: payload.message || null,
    pools,
    poolStatuses,
    monthlyUsage: Array.isArray(payload.monthlyUsage)
      ? payload.monthlyUsage.map(normalizeMonthlyUsageRow)
      : [],
  };
};

function formatPoolHours(seconds) {
  const h = toNumber(seconds, 0) / 3600;
  if (h < 1 && h > 0) return `${Math.round(h * 60)} min`;
  return `${(Math.round(h * 10) / 10).toFixed(1)} h`;
}

function poolStatusMeta(status) {
  switch (String(status || '').toLowerCase()) {
    case 'active':
      return { label: 'Active', color: '#047857', bg: '#ecfdf5' };
    case 'depleted':
      return { label: 'Depleted', color: '#b45309', bg: '#fffbeb' };
    default:
      return { label: 'Inactive', color: '#64748b', bg: '#f1f5f9' };
  }
}

function MonthlyUsageTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: 'white',
        border: '1px solid #e2e8f0',
        boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
        minWidth: 180,
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e', mb: 0.75 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 11, color: '#64748b' }}>
        Main: <strong>{row.mainUsedFormatted}</strong>
      </Typography>
      <Typography sx={{ fontSize: 11, color: '#64748b' }}>
        Add-on: <strong>{row.addonUsedFormatted}</strong>
      </Typography>
      <Typography sx={{ fontSize: 11, color: '#64748b' }}>
        Admin: <strong>{row.adminUsedFormatted}</strong>
      </Typography>
      <Typography sx={{ fontSize: 11, color: '#1a1a2e', mt: 0.5, fontWeight: 600 }}>
        Total: {row.totalUsedFormatted}
      </Typography>
    </Box>
  );
}

function MonthlyUsageSection({ monthlyUsage, planLimitHours }) {
  const rows = Array.isArray(monthlyUsage) ? monthlyUsage : [];
  const hasUsage = rows.some((row) => row.usedSeconds > 0);
  const peakRow = rows.reduce(
    (best, row) => (row.totalHours > (best?.totalHours || 0) ? row : best),
    null,
  );
  const avgHours =
    rows.length > 0
      ? rows.reduce((sum, row) => sum + row.totalHours, 0) / rows.length
      : 0;
  const chartMax = Math.max(
    planLimitHours || 0,
    ...rows.map((row) => row.totalHours),
    1,
  );
  const yMax = Math.ceil(chartMax / 4) * 4 || 4;

  return (
    <Paper elevation={0} sx={{ ...styles.panel, mt: 3 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Typography sx={styles.panelTitle}>
          <CalendarMonthIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
          Monthly usage history
        </Typography>
        {hasUsage ? (
          <Typography sx={{ fontSize: 12, color: '#6c6c8a', fontWeight: 500 }}>
            Avg {(Math.round(avgHours * 10) / 10).toFixed(1)} h
            {peakRow?.totalHours > 0 ? ` · peak ${peakRow.totalUsedFormatted} (${peakRow.label})` : ''}
          </Typography>
        ) : null}
      </Box>
      <Typography sx={{ fontSize: 13, color: '#6c6c8a', mb: 2 }}>
        Per billing month breakdown — main, add-on, and admin bonus time consumed.
      </Typography>

      {!hasUsage ? (
        <Box sx={styles.monthlyEmpty}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>
            No monthly usage recorded yet
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#6c6c8a', maxWidth: 360 }}>
            Usage by month will appear here once disinfection sessions are logged.
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ width: '100%', height: 280, mb: 2.5 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickLine={false}
                />
                <YAxis
                  domain={[0, yMax]}
                  tickFormatter={(v) => `${v}h`}
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<MonthlyUsageTooltip />} />
                <Legend
                  wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                  formatter={(value) => (
                    <span style={{ color: '#475569' }}>{value}</span>
                  )}
                />
                <Bar dataKey="mainHours" stackId="usage" fill="#2563eb" name="Main" radius={[0, 0, 0, 0]} />
                <Bar dataKey="addonHours" stackId="usage" fill="#7c3aed" name="Add-on" radius={[0, 0, 0, 0]} />
                <Bar dataKey="adminHours" stackId="usage" fill="#ea580c" name="Admin bonus" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>

          <Box sx={styles.monthlyTableWrap}>
            <Box sx={styles.monthlyTableHeader}>
              <Typography sx={styles.monthlyColPeriod}>Period</Typography>
              <Typography sx={styles.monthlyCol}>Main</Typography>
              <Typography sx={styles.monthlyCol}>Add-on</Typography>
              <Typography sx={styles.monthlyCol}>Admin</Typography>
              <Typography sx={styles.monthlyColTotal}>Total</Typography>
            </Box>
            {rows.map((row) => (
              <Box key={`${row.year}-${row.month}-${row.planMonth}`} sx={styles.monthlyTableRow}>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>
                    {row.label}
                  </Typography>
                  {row.monthName && row.year ? (
                    <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                      {row.monthName} {row.year}
                    </Typography>
                  ) : null}
                </Box>
                <Typography sx={styles.monthlyCol}>{row.mainUsedFormatted}</Typography>
                <Typography sx={styles.monthlyCol}>{row.addonUsedFormatted}</Typography>
                <Typography sx={styles.monthlyCol}>{row.adminUsedFormatted}</Typography>
                <Typography sx={styles.monthlyColTotal}>{row.totalUsedFormatted}</Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Paper>
  );
}

function PoolCard({ title, subtitle, remainingSeconds, quotaSeconds, usedSeconds, status, accentColor }) {
  const safeQuota = quotaSeconds > 0 ? quotaSeconds : 1;
  const pct = Math.min(100, Math.round((remainingSeconds / safeQuota) * 100));
  const meta = poolStatusMeta(status);

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        bgcolor: '#f8fafc',
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}>{title}</Typography>
          {subtitle ? (
            <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.25 }}>{subtitle}</Typography>
          ) : null}
        </Box>
        <Chip
          label={meta.label}
          size="small"
          sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: meta.bg, color: meta.color }}
        />
      </Box>
      <Typography sx={{ fontSize: 22, fontWeight: 800, color: accentColor }}>
        {formatPoolHours(remainingSeconds)}
      </Typography>
      <Typography sx={{ fontSize: 11, color: '#64748b', mb: 1 }}>
        remaining · used {formatPoolHours(usedSeconds)}
        {quotaSeconds > 0 ? ` · quota ${formatPoolHours(quotaSeconds)}` : ''}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 8,
          borderRadius: 999,
          bgcolor: '#e2e8f0',
          '& .MuiLinearProgress-bar': { borderRadius: 999, bgcolor: accentColor },
        }}
      />
    </Box>
  );
}

function getAlertMeta(status, backendAlert) {
  if (backendAlert) {
    const isExpired = /expired|no active subscription/i.test(backendAlert);
    const isExhausted = /no active time|exhausted|limit/i.test(backendAlert);
    if (isExpired || isExhausted) {
      return {
        icon: <ErrorIcon sx={{ fontSize: 22 }} />,
        title: 'Action required',
        message: backendAlert,
        gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        bgGradient: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
        color: '#dc2626',
      };
    }
    return {
      icon: <WarningIcon sx={{ fontSize: 22 }} />,
      title: 'Usage notice',
      message: backendAlert,
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      bgGradient: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
      color: '#d97706',
    };
  }

  switch (status) {
    case 'exceeded':
      return {
        icon: <ErrorIcon sx={{ fontSize: 22 }} />,
        title: 'Limit Exceeded',
        message: 'You have reached your plan limit. Upgrade or contact support to continue usage.',
        gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        bgGradient: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
        color: '#dc2626',
      };
    case 'warning':
      return {
        icon: <WarningIcon sx={{ fontSize: 22 }} />,
        title: 'Near Usage Limit',
        message: 'You are close to your monthly limit. Plan an upgrade to avoid interruptions.',
        gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        bgGradient: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
        color: '#d97706',
      };
    default:
      return {
        icon: <CheckCircleIcon sx={{ fontSize: 22 }} />,
        title: 'Usage Healthy',
        message: 'Usage is within your current plan limits.',
        gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        bgGradient: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
        color: '#059669',
      };
  }
}

const RING_RADIUS = 52;

function getUsedAccentColor(percent) {
  if (percent <= 0) return '#94a3b8';
  if (percent >= 100) return '#ef4444';
  if (percent >= 80) return '#f59e0b';
  return '#3b82f6';
}

function UsageRing({ percent, label, color }) {
  const p = Math.max(0, Math.min(100, percent));
  const circumference = 2 * Math.PI * RING_RADIUS;
  const offset = circumference - (p / 100) * circumference;
  const arcColor = p > 0 ? color : '#e2e8f0';

  return (
    <Box sx={styles.ringContainer}>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <Box sx={{ position: 'relative', width: 160, height: 160 }}>
          <svg width="160" height="160" viewBox="0 0 120 120">
            <circle
              cx="60"
              cy="60"
              r={RING_RADIUS}
              fill="none"
              stroke="#e2e8f0"
              strokeWidth="8"
            />
            {p > 0 ? (
              <circle
                cx="60"
                cy="60"
                r={RING_RADIUS}
                fill="none"
                stroke={arcColor}
                strokeWidth="8"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 0.5s ease' }}
                transform="rotate(-90 60 60)"
              />
            ) : null}
          </svg>
          <Box sx={styles.ringInner}>
            <Typography sx={styles.ringValue}>{p}%</Typography>
            <Typography sx={styles.ringLabel}>{label}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function MetricCard({ label, value, icon: Icon, color, subtitle }) {
  return (
    <Box
      sx={{
        ...styles.metricCard,
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={styles.metricLabel}>{label}</Typography>
          <Typography sx={styles.metricValue}>{value}</Typography>
          {subtitle && <Typography sx={styles.metricSubtitle}>{subtitle}</Typography>}
        </Box>
        <Avatar sx={{ bgcolor: `${color}15`, color: color, width: 48, height: 48 }}>
          <Icon />
        </Avatar>
      </Box>
    </Box>
  );
}

function PlanUsageDashboard({ robotId, onPlanNameChange, embedded = false, showBack = false }) {
  const navigate = useNavigate();
  const [planUsage, setPlanUsage] = useState({
    loading: true,
    error: null,
    data: DEFAULT_PLAN_USAGE,
  });
  const fetchUserPlanUsage = async () => {
    setPlanUsage((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await getUserPlanUsage();
      const normalizedData = normalizePlanUsage(response.data, robotId);
      setPlanUsage({ loading: false, error: null, data: normalizedData });
      onPlanNameChange?.(normalizedData.planName || null);
    } catch (error) {
      setPlanUsage((prev) => ({
        loading: false,
        error:
          error.response?.data?.message ||
          'Unable to load plan usage right now. Please try again.',
        data: prev.data || DEFAULT_PLAN_USAGE,
      }));
      onPlanNameChange?.(null);
    }
  };

  useEffect(() => {
    fetchUserPlanUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [robotId]);

  const usage = planUsage.data || DEFAULT_PLAN_USAGE;
  const usedPercent = Math.max(
    0,
    Math.min(
      100,
      Math.round(
        toNumber(
          usage.usagePercentage,
          usage.limit > 0 ? Math.min((usage.usage / usage.limit) * 100, 100) : 0,
        ),
      ),
    ),
  );
  const remainingPercent = Math.max(100 - usedPercent, 0);
  const alert = getAlertMeta(usage.status, usage.alert || usage.message);
  const displayRobotId = usage.robotId || robotId;
  const hasUsage = toNumber(usage.usedSeconds, 0) > 0 || usedPercent > 0;
  const usedAccent = getUsedAccentColor(usedPercent);

  if (planUsage.loading) {
    return (
      <Box sx={embedded ? styles.embedWrap : styles.pageWrap}>
        <Box sx={styles.loaderBox}>
          <div className="pulse-animation">
            <CircularProgress size={56} sx={{ color: theme.colors.brandPrimary, mb: 2 }} />
          </div>
          <Typography sx={{ fontSize: 14, color: theme.colors.textSecondary }}>
            Loading usage dashboard...
          </Typography>
        </Box>
      </Box>
    );
  }

  const getProgressColor = () => {
    if (usedPercent >= 100) return '#ef4444';
    if (usedPercent >= 80) return '#f59e0b';
    return '#10b981';
  };

  return (
    <Box sx={embedded ? styles.embedWrap : styles.pageWrap}>
      <Box sx={styles.shell}>
        {showBack && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={styles.backBtn}
          >
            Back to Dashboard
          </Button>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography sx={styles.pageTitle}>
            {embedded ? 'Plan Usage Analytics' : 'Usage Intelligence Dashboard'}
          </Typography>
          <Typography sx={styles.pageSubtitle}>
            Monitor your consumption patterns and stay within limits
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mt: 2 }}>
            <Chip
              icon={<WorkspacePremiumIcon />}
              label={usage.planName}
              sx={{
                bgcolor: 'white',
                border: `1px solid ${theme.colors.borderDefault}`,
                fontWeight: 600,
                fontSize: 13,
                '& .MuiChip-icon': { color: accent },
              }}
            />
            {displayRobotId && (
              <Chip
                label={`Robot: ${displayRobotId}`}
                sx={{
                  bgcolor: '#f1f5f9',
                  fontSize: 12,
                  fontWeight: 500,
                }}
              />
            )}
            <Chip
              label={
                !usage.canDisinfect
                  ? 'No time left'
                  : usage.status === 'ok'
                    ? 'Active'
                    : usage.status === 'warning'
                      ? 'Near limit'
                      : 'Exceeded'
              }
              sx={{
                bgcolor: alert.bgGradient,
                color: alert.color,
                fontWeight: 600,
                fontSize: 12,
              }}
            />
            {usage.planpaymentStatus ? (
              <Chip
                label={String(usage.planpaymentStatus).replace(/_/g, ' ')}
                sx={{ bgcolor: '#f1f5f9', fontSize: 12, fontWeight: 500, textTransform: 'capitalize' }}
              />
            ) : null}
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={styles.panel}>
              <Typography sx={styles.panelTitle}>
                <SpeedIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                Usage Overview
              </Typography>
              <Box sx={styles.ringsRow}>
                <UsageRing percent={usedPercent} label="Used" color={usedAccent} />
                <UsageRing percent={remainingPercent} label="Remaining" color="#22c55e" />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={styles.panel}>
              <Typography sx={styles.panelTitle}>
                <TimerIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                Key Metrics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <MetricCard
                    label="Total time used"
                    value={usage.usedTimeFormatted}
                    icon={TrendingUpIcon}
                    color={hasUsage ? usedAccent : '#94a3b8'}
                    subtitle={
                      usage.pools
                        ? 'Across all pools'
                        : `of ${Math.round(usage.limit)}h monthly`
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <MetricCard
                    label="Total remaining"
                    value={usage.remainingTimeFormatted}
                    icon={TrendingDownIcon}
                    color="#10b981"
                    subtitle={usage.canDisinfect ? 'Available to disinfect' : 'Pools exhausted'}
                  />
                </Grid>
                {usage.planAddonStatus && (
                  <Grid item xs={6}>
                    <MetricCard
                      label="Add-on Status"
                      value={usage.planAddonStatus}
                      icon={WorkspacePremiumIcon}
                      color="#8b5cf6"
                    />
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        {usage.pools ? (
          <Paper elevation={0} sx={{ ...styles.panel, mt: 3 }}>
            <Typography sx={styles.panelTitle}>
              <WorkspacePremiumIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
              Time pools
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#6c6c8a', mb: 2 }}>
              Main hours reset each billing month. Add-on and admin bonus time persist until used
              (consumed after main time).
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <PoolCard
                  title="Main plan"
                  subtitle="Resets monthly"
                  remainingSeconds={usage.pools.main?.remainingSeconds}
                  quotaSeconds={usage.pools.main?.quotaSeconds}
                  usedSeconds={usage.pools.main?.usedSeconds}
                  status={usage.pools.main?.status || usage.poolStatuses?.main}
                  accentColor="#2563eb"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PoolCard
                  title="Add-on"
                  subtitle="Purchased packs"
                  remainingSeconds={usage.pools.addon?.remainingSeconds}
                  quotaSeconds={usage.pools.addon?.purchasedSeconds}
                  usedSeconds={usage.pools.addon?.usedSeconds}
                  status={usage.pools.addon?.status || usage.poolStatuses?.addon}
                  accentColor="#7c3aed"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PoolCard
                  title="Admin bonus"
                  subtitle="Separate pool — does not reset"
                  remainingSeconds={usage.pools.admin?.remainingSeconds}
                  quotaSeconds={usage.pools.admin?.grantedSeconds}
                  usedSeconds={usage.pools.admin?.usedSeconds}
                  status={usage.pools.admin?.status || usage.poolStatuses?.admin}
                  accentColor="#ea580c"
                />
              </Grid>
            </Grid>
          </Paper>
        ) : null}

        <MonthlyUsageSection monthlyUsage={usage.monthlyUsage} planLimitHours={usage.limit} />

        <Paper elevation={0} sx={styles.progressPanel}>
          <Box sx={styles.progressHeader}>
            <Typography sx={{ fontWeight: 600, color: '#1a1a2e' }}>
              Combined consumption
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography sx={{ fontSize: 13, color: '#6c6c8a' }}>
                Used: <strong style={{ color: '#ef4444' }}>{usedPercent}%</strong>
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#6c6c8a' }}>
                Remaining: <strong style={{ color: '#22c55e' }}>{remainingPercent}%</strong>
              </Typography>
            </Box>
          </Box>
          <Box sx={{ position: 'relative' }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(usedPercent, 100)}
              sx={{
                height: 12,
                borderRadius: 6,
                bgcolor: '#e2e8f0',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 6,
                  background: `linear-gradient(90deg, ${getProgressColor()}CC, ${getProgressColor()})`,
                },
              }}
            />
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ ...styles.alertPanel, background: alert.bgGradient }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ color: alert.color }}>{alert.icon}</Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 15, color: alert.color }}>
                {alert.title}
              </Typography>
              <Typography sx={{ fontSize: 13, color: alert.color, opacity: 0.9 }}>
                {alert.message}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {planUsage.error && (
          <Paper elevation={0} sx={styles.errorBox}>
            <Typography sx={{ fontSize: 14, color: '#991b1b' }}>{planUsage.error}</Typography>
          </Paper>
        )}

        <style>
          {`
            .pulse-animation {
              animation: pulse 1.5s ease-in-out infinite;
            }
            @keyframes pulse {
              0%, 100% { opacity: 1; transform: scale(1); }
              50% { opacity: 0.7; transform: scale(0.98); }
            }
          `}
        </style>
      </Box>
    </Box>
  );
}

/** Embedded in RobotDetailView */
export function PlanUsageView({ robotId, onPlanNameChange }) {
  return (
    <PlanUsageDashboard
      robotId={robotId}
      onPlanNameChange={onPlanNameChange}
      embedded
    />
  );
}

/** Standalone plan usage page */
export default function PlanUsage() {
  return <PlanUsageDashboard showBack />;
}

const styles = {
  pageWrap: {
    position: 'relative',
    minHeight: '100vh',
    py: { xs: 2, md: 4 },
    px: { xs: 2, md: 3 },
    background: 'linear-gradient(135deg, #f0f4f8 0%, #e9edf2 100%)',
  },
  embedWrap: {
    py: 0,
    px: 0,
    minHeight: 'auto',
    background: 'transparent',
  },
  shell: {
    maxWidth: 1100,
    mx: 'auto',
  },
  loaderBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
    p: 4,
    borderRadius: '28px',
    bgcolor: 'white',
    border: '1px solid rgba(0,0,0,0.05)',
    boxShadow: '0 20px 35px -12px rgba(0,0,0,0.1)',
    maxWidth: 420,
    mx: 'auto',
  },
  backBtn: {
    mb: 2.5,
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: '12px',
    px: 2,
    py: 0.75,
    color: '#1a1a2e',
    borderColor: '#e2e8f0',
    bgcolor: 'white',
    '&:hover': {
      bgcolor: '#f8fafc',
      borderColor: '#cbd5e1',
    },
  },
  pageTitle: {
    fontSize: { xs: '1.75rem', md: '2.25rem' },
    fontWeight: 800,
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
    letterSpacing: '-0.02em',
    mb: 0.5,
  },
  pageSubtitle: {
    fontSize: 14,
    color: '#6c6c8a',
  },
  panel: {
    p: { xs: 2.5, md: 3 },
    borderRadius: '24px',
    border: '1px solid rgba(0,0,0,0.05)',
    bgcolor: 'white',
    boxShadow: '0 8px 20px rgba(0,0,0,0.04)',
    height: '100%',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 12px 28px rgba(0,0,0,0.08)',
    },
  },
  panelTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#1a1a2e',
    mb: 2.5,
    display: 'flex',
    alignItems: 'center',
  },
  ringsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 3,
  },
  ringContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  ringInner: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
  },
  ringValue: {
    fontSize: '1.5rem',
    fontWeight: 800,
    color: '#1a1a2e',
    lineHeight: 1.2,
  },
  ringLabel: {
    fontSize: '0.75rem',
    color: '#6c6c8a',
    fontWeight: 500,
  },
  metricCard: {
    p: 1.5,
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    bgcolor: '#f8fafc',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  },
  metricLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#6c6c8a',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    mb: 0.5,
  },
  metricValue: {
    fontSize: 20,
    fontWeight: 800,
    color: '#1a1a2e',
  },
  metricSubtitle: {
    fontSize: 10,
    color: '#94a3b8',
    mt: 0.25,
  },
  progressPanel: {
    mt: 3,
    p: { xs: 2, md: 2.5 },
    borderRadius: '20px',
    border: '1px solid rgba(0,0,0,0.05)',
    bgcolor: 'white',
    boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
  },
  progressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 1.5,
    flexWrap: 'wrap',
    gap: 1,
  },
  alertPanel: {
    mt: 2.5,
    p: 2.5,
    borderRadius: '20px',
    border: '1px solid rgba(0,0,0,0.05)',
    transition: 'all 0.2s ease',
  },
  errorBox: {
    mt: 2,
    p: 2,
    borderRadius: '16px',
    bgcolor: '#fef2f2',
    border: '1px solid #fecaca',
  },
  monthlyEmpty: {
    minHeight: 180,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: 1,
    borderRadius: '16px',
    border: '1px dashed #e2e8f0',
    bgcolor: '#f8fafc',
    px: 2,
  },
  monthlyTableWrap: {
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
  },
  monthlyTableHeader: {
    display: 'grid',
    gridTemplateColumns: '1.4fr repeat(3, 1fr) 1.1fr',
    gap: 1,
    px: 2,
    py: 1.25,
    bgcolor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  },
  monthlyTableRow: {
    display: 'grid',
    gridTemplateColumns: '1.4fr repeat(3, 1fr) 1.1fr',
    gap: 1,
    px: 2,
    py: 1.25,
    borderBottom: '1px solid #f1f5f9',
    alignItems: 'center',
    '&:last-child': { borderBottom: 'none' },
  },
  monthlyColPeriod: {
    fontSize: 11,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  },
  monthlyCol: {
    fontSize: 13,
    color: '#334155',
    fontWeight: 500,
  },
  monthlyColTotal: {
    fontSize: 13,
    color: '#1a1a2e',
    fontWeight: 700,
  },
};